import {
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  Paper,
  Select,
  Switch,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { MailOutlineRounded as MailIcon } from '@material-ui/icons';

import { User } from '../../types/user.type';
import { UserRole, UserRoles } from '../../types/user-role.type';
import { USER_ROLE_LABELS } from '../../constants/user-role-labels.constants';
import { UserFieldChange } from '../../hooks/use-user-edit-form.hook';
import { useSendEcoWelcomeEmail } from '../../hooks/send-eco-welcome-email.hook';

const useStyles = makeStyles((theme) => ({
  paperContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '1rem 0.75rem',
  },
  profileRow: {
    display: 'flex',
    gap: '1rem',

    '& > *': {
      flex: 1,
    },
  },
  ecoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  preferences: {
    display: 'flex',
    flexDirection: 'column',
  },
  preferenceRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',

    '& > div': {
      display: 'flex',
      flexDirection: 'column',

      '& > span:first-child': {
        fontSize: '0.875rem',
        fontWeight: 600,
        color: '#424242',
      },

      '& > span:last-child': {
        fontSize: 12,
        color: '#757575',
      },
    },
  },
  switchBase: {
    '&$switchChecked': {
      color: theme.palette.admin.primary.main,
    },
    '&$switchChecked + $switchTrack': {
      backgroundColor: theme.palette.admin.primary.main,
    },
  },
  switchChecked: {},
  switchTrack: {},
}));

type Preference = { field: 'newsLetter' | 'groupLabels'; title: string; description: string };

const PREFERENCES: Preference[] = [
  { field: 'newsLetter', title: 'Newsletter', description: "Reçoit les emails d'actualité et de promotion" },
  {
    field: 'groupLabels',
    title: 'Étiquettes groupées',
    description: "Regroupe les étiquettes de ses commandes à l'impression",
  },
];

interface Props {
  user: User;
  updatedUser: User;
  roles: UserRole[];
  isRoleLocked: boolean;
  roleHelperText?: string;
  ecoPointsError?: string;
  onFieldChange: (change: UserFieldChange) => void;
}

export function UserProfileCard({
  user,
  updatedUser,
  roles,
  isRoleLocked,
  roleHelperText,
  ecoPointsError,
  onFieldChange,
}: Props) {
  const classes = useStyles();
  const { sendEcoWelcomeEmail, isSubmitting: isSendingWelcome } = useSendEcoWelcomeEmail();

  return (
    <section>
      <h4>Profil</h4>
      <Paper className={classes.paperContent}>
        <section className={classes.profileRow}>
          <FormControl variant="outlined" size="small" disabled={isRoleLocked}>
            <InputLabel htmlFor="user-role">Rôle</InputLabel>
            <Select
              native
              label="Rôle"
              inputProps={{ id: 'user-role' }}
              value={updatedUser.role}
              onChange={(event) => onFieldChange({ field: 'role', value: event.target.value as UserRole })}
            >
              {roles.map((role) => (
                <option key={role} value={role}>
                  {USER_ROLE_LABELS[role]}
                </option>
              ))}
            </Select>
            {roleHelperText && <FormHelperText>{roleHelperText}</FormHelperText>}
          </FormControl>
          <TextField
            id="user-invoicing-number"
            label="N° de facturation"
            type="text"
            size="small"
            variant="outlined"
            value={updatedUser.invoicingNumber ?? ''}
            onChange={(event) => onFieldChange({ field: 'invoicingNumber', value: event.target.value })}
          />
        </section>

        {updatedUser.role === UserRoles.ECO && (
          <section className={classes.ecoRow}>
            <TextField
              id="user-eco-points"
              label="Éco-points"
              type="number"
              size="small"
              variant="outlined"
              inputProps={{ step: 0.01, min: 0 }}
              InputProps={{ endAdornment: <InputAdornment position="end">pts</InputAdornment> }}
              error={Boolean(ecoPointsError)}
              helperText={ecoPointsError}
              value={updatedUser.ecoPoints ?? 0}
              onChange={(event) => onFieldChange({ field: 'ecoPoints', value: Number(event.target.value) })}
            />
            {user.role === UserRoles.ECO && user.isActive && (
              <Button
                size="small"
                variant="outlined"
                disabled={isSendingWelcome}
                startIcon={isSendingWelcome ? <CircularProgress size={16} color="inherit" /> : <MailIcon />}
                onClick={() => sendEcoWelcomeEmail(user.id)}
              >
                Renvoyer le mail de bienvenue
              </Button>
            )}
          </section>
        )}

        <Divider />

        <section className={classes.preferences}>
          {PREFERENCES.map((preference) => (
            <div key={preference.field} className={classes.preferenceRow}>
              <div>
                <span>{preference.title}</span>
                <span>{preference.description}</span>
              </div>
              <Switch
                size="small"
                color="default"
                classes={{
                  switchBase: classes.switchBase,
                  checked: classes.switchChecked,
                  track: classes.switchTrack,
                }}
                inputProps={{ 'aria-label': preference.title }}
                checked={Boolean(updatedUser[preference.field])}
                onChange={(event) => onFieldChange({ field: preference.field, value: event.target.checked })}
              />
            </div>
          ))}
        </section>
      </Paper>
    </section>
  );
}
